import { FC, useMemo } from 'react';
import { $Formutil, connect } from 'react-bootstrap-formutil';
import FamilyInfo from 'components/forms/FamilyInfo';
import HomeAddress, { HomeAddressProps } from 'components/forms/HomeAddress';
import IdNumber from 'components/forms/IdNumber';
import type { IdTypeFormutil, IdTypeProps } from 'components/forms/IdType';
import IdType from 'components/forms/IdType';
import IssueCountry, { IssueCountryFormutil } from 'components/forms/IssueCountry';
import { getNameType, RealNameInfoFormutil } from 'components/forms/RealNameInfo';
import UI from 'stores/ui';
import { CountryTypeEnum } from 'types/common';
import { checkFourCountryIsChn } from 'utils/helper';
import Birthday from '../components/Birthday';
import Country, { CountryProps } from '../components/Country';
import Gender from '../components/Gender';
import { BasePersonalInfoFormutil, BasePersonalInfoProps } from '../type';

export type NonSSNPersonalInfoFormutil = RealNameInfoFormutil &
    BasePersonalInfoFormutil &
    IssueCountryFormutil &
    IdTypeFormutil;

export interface NonSSNProps extends BasePersonalInfoProps {
    legalResidenceCountry: string;
    taxResidenceCountry: string;
    countryProps?: Omit<CountryProps, '$formutil'>;
    homeAddressProps?: Partial<Omit<HomeAddressProps, '$formutil' | 'isSSN'>>;
    hideHomeAddress?: boolean;
    idTypeProps?: IdTypeProps;
    birthdayProps?: { disabled?: boolean };
    $formutil: $Formutil<NonSSNPersonalInfoFormutil>;
}

const NonSSN: FC<NonSSNProps> = ({
    idNumberProps,
    disabled,
    birthdayProps,
    countryProps,
    idTypeProps,
    homeAddressProps,
    hideHomeAddress,
    familyInfoProps,
    legalResidenceCountry,
    taxResidenceCountry,
    $formutil,
    type
}) => {
    const {
        country: formCountry,
        issue_country: formIssueCountry,
        id_type: formIdTyp,
        name_type: formNameType
    } = $formutil.$params;
    const { disabled: idNumberDisabled, ...restIdNumberOptions } = idNumberProps;
    const { disabled: countryDisabled, countrySelectProps, ...restCountryProps } = countryProps || {};
    const { disabled: idTypeDisabled, ...restIdTypeOptions } = idTypeProps || {};
    const { disabled: homeAddressDisabled, ...restHomeAddressOptions } = homeAddressProps || {};
    // familyInfo缺失是允许单独编辑的，familyInfoDisabled优先级比disabled高，默认取disabled
    const { disabled: familyInfoDisabled = disabled, ...restFamilyInfoOptions } = familyInfoProps || {};
    const { disabled: birthdayDisabled = disabled } = birthdayProps || {};
    const isFourChn = useMemo(() => {
        return checkFourCountryIsChn({
            country: formCountry,
            issueCountry: formIssueCountry,
            legalResidenceCountry,
            taxResidenceCountry
        });
    }, [formCountry, formIssueCountry, legalResidenceCountry, taxResidenceCountry]);

    return (
        <>
            {type === 'baseinfo2' ? null : (
                <>
                    <Birthday disabled={birthdayDisabled} />
                    <Gender disabled={disabled} />
                </>
            )}
            {type === 'baseinfo' ? null : (
                <>
                    <Country
                        isSSN={false}
                        disabled={countryDisabled || disabled}
                        $onFieldChange={value => {
                            const newNameType = getNameType({ idType: formIdTyp, country: value });

                            if (formNameType !== newNameType) {
                                $formutil.$setValues({
                                    name_type: newNameType
                                });
                            }
                        }}
                        countrySelectProps={{
                            type: CountryTypeEnum.NON_SSN_CITIZENSHIP,
                            ...countrySelectProps
                        }}
                        {...restCountryProps}
                    />
                    {/* 四个国家都是中国仍保持第二期的开户流程 */}
                    {isFourChn ? (
                        <>
                            <IdNumber
                                needHide
                                autoComplete={UI.isMobile ? 'new-password' : 'one-time-code'}
                                disabled={idNumberDisabled || disabled}
                                {...restIdNumberOptions}
                            />
                            <FamilyInfo disabled={familyInfoDisabled} {...restFamilyInfoOptions} />
                            {/* 从非四个中国切换为四个中国，开户流程里不展示签发国字段，直接去掉issue_country表单，值丢失会影响判断是否为四个中国，所以用了隐藏表单 */}
                            <IssueCountry
                                type="hidden"
                                isSSN={false}
                                countrySelectProps={{
                                    type: CountryTypeEnum.ALL_COUNTRY,
                                    limitCountryList: [formCountry, legalResidenceCountry]
                                }}
                            />
                            {hideHomeAddress ? null : (
                                <HomeAddress
                                    isSSN={false}
                                    hideMailAddress
                                    hasUSDeliveryAddress
                                    disabled={homeAddressDisabled || disabled}
                                    legalResidenceCountry={legalResidenceCountry}
                                    {...restHomeAddressOptions}
                                />
                            )}
                        </>
                    ) : (
                        <>
                            <FamilyInfo hideTitle disabled={familyInfoDisabled} {...restFamilyInfoOptions} />
                            <IssueCountry
                                disabled={disabled}
                                isSSN={false}
                                countrySelectProps={{
                                    type: CountryTypeEnum.ALL_COUNTRY,
                                    limitCountryList: [formCountry, legalResidenceCountry]
                                }}
                            />
                            <IdType
                                disabled={idTypeDisabled || disabled}
                                issueCountry={formIssueCountry}
                                {...restIdTypeOptions}
                            />
                            <IdNumber
                                needHide
                                autoComplete={UI.isMobile ? 'new-password' : 'one-time-code'}
                                disabled={idNumberDisabled || disabled}
                                {...restIdNumberOptions}
                            />
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default connect(NonSSN);
